import { InjectionToken } from '@angular/core';
import { EMPTY, Observable, of } from 'rxjs';

import { TimezoneEntry } from '@ninety/platform/models/_shared/timezone-select';
import { User } from '@ninety/platform/models/_shared/user';
import { UserKpiOrdinal } from '@ninety/platform/models/_shared/user-kpi-ordinal.model';
import { UserSettings } from '@ninety/platform/models/_shared/user-settings';
import { PeriodInterval } from '@ninety/platform/models/scorecard/period-interval.enum';
import { NewUserDto } from '@ninety/platform/models/user/new-user.dto';

import { UserListRequest } from '../app-entities/user-list/api/user-list-request.model';
import { UserListModel } from '../app-entities/user-list/api/user-list.model';

/**
 * Narrow slice of `UserService` used by the state effects that inject it — `TeamListStateEffects` +
 * `UserListStateEffects` (DEV-26105 T5: `getUserList`/`update`) and `UsersStateEffects` (T6c: the rest).
 * `UserService` lives in `shared/ui/core` and pulls dialog components, so it can't move; importing it
 * here would reopen the `platform/state -> shared/ui/core` edge. The effects inject this token and the
 * app composition root binds it via `{ provide: USER_SERVICE, useExisting: UserService }` — so every
 * member here must stay structurally assignable to the matching `UserService` method.
 */
export interface UserControl {
  getUserList(request: Pick<UserListRequest, 'sort'>): Observable<UserListModel[]>;
  update(update: Partial<User>, userId?: string): Observable<User>;
  updateQuietly(userId: string, update: Partial<User>): Observable<void>;
  updateUserKpiOrder(params: {
    userId: string;
    userKpiOrdinals: UserKpiOrdinal[];
    periodInterval: PeriodInterval;
  }): Observable<unknown>;
  updateUserSettings(update?: Partial<UserSettings>): Observable<User>;
  deleteUser(userId: string): Observable<void>;
  deactivateUser(userId: string): Observable<unknown>;
  checkForHelpfuls(emails: string[]): Observable<Record<string, boolean>>;
  createUsersV2(newUsers: NewUserDto[]): Observable<User[]>;
  showTimezoneUpdateDialog(browserTimezoneEntry: TimezoneEntry): Observable<User>;
  updateUserSettingsToBrowserTimezone(payload: TimezoneEntry): Observable<User>;
}

/**
 * Defaults to a no-op so the effects never hit a null injector in test/storybook contexts. The app
 * composition root overrides it with the real `UserService`.
 */
export const USER_SERVICE = new InjectionToken<UserControl>('USER_SERVICE', {
  providedIn: 'root',
  factory: (): UserControl => ({
    getUserList: (): Observable<UserListModel[]> => of([]),
    update: (): Observable<User> => EMPTY,
    updateQuietly: (): Observable<void> => EMPTY,
    updateUserKpiOrder: (): Observable<unknown> => EMPTY,
    updateUserSettings: (): Observable<User> => EMPTY,
    deleteUser: (): Observable<void> => EMPTY,
    deactivateUser: (): Observable<unknown> => EMPTY,
    checkForHelpfuls: (): Observable<Record<string, boolean>> => of({}),
    createUsersV2: (): Observable<User[]> => of([]),
    showTimezoneUpdateDialog: (): Observable<User> => EMPTY,
    updateUserSettingsToBrowserTimezone: (): Observable<User> => EMPTY,
  }),
});
